import { Pencil, RotateCcw, ExternalLink, Move } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtM } from "@/lib/geo";
import { STAFF_GROUPS, StaffMini } from "@/components/map/StaffMarkers";

const hm = (m) => { const x = ((Math.round(m) % 1440) + 1440) % 1440; return `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`; };
const Head = ({ title, sub }) => (
  <div className="mb-3">
    <div className="font-display text-lg font-bold text-white leading-tight">{title}</div>
    {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

export const DetourPanel = ({ d, edited, canEdit, onEdit, onReset, onOpenCut }) => (
  <div className="space-y-3" data-testid="detour-panel">
    <span className="font-mono text-[10px] tracking-widest text-green-400">DESVÍO</span>
    <Head title={d.element_name} sub={edited ? "Trazado editado a mano" : d.status === "ok" ? "Calculado automáticamente" : d.status === "parcial" ? "Cruza el recorrido: solo cuando haya pasado la comitiva" : `Estado: ${d.status}`} />
    {d.pending && <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2" data-testid="detour-pending-hint">Desvío propuesto: acepte el corte previo para incluirlo en el plan y poder editar su trazado.</div>}
    <div className="grid grid-cols-2 gap-2 text-xs">
      <div className="bg-[#0b1329] rounded-lg p-2"><div className="text-slate-500 text-[10px]">DISTANCIA</div><b className="text-white" data-testid="detour-distance">{fmtM(d.distance_m || 0)}</b></div>
      <div className="bg-[#0b1329] rounded-lg p-2"><div className="text-slate-500 text-[10px]">TIEMPO</div><b className="text-white">{d.duration_min || 0} min</b></div>
    </div>
    {d.streets?.length > 0 && !edited && <div className="text-xs text-slate-300">{d.streets.join(" → ")}</div>}
    {d.oneway_conflicts?.length > 0 && <div className="text-xs text-red-300">Contra sentido: {d.oneway_conflicts.map((c) => c.name).join(", ")}</div>}
    {canEdit && !d.pending && (
      <div className="flex flex-wrap gap-2 pt-1">
        {!edited
          ? <Button size="sm" className="h-8 bg-green-500 hover:bg-green-400 text-slate-950" onClick={onEdit} data-testid="detour-edit-button"><Pencil className="w-3.5 h-3.5 mr-1" /> Editar trazado</Button>
          : <Button size="sm" variant="outline" className="h-8" onClick={onReset} data-testid="detour-reset-button"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Volver al automático</Button>}
        <Button size="sm" variant="outline" className="h-8" onClick={onOpenCut} data-testid="detour-open-cut-button"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Ver corte</Button>
      </div>
    )}
    {edited && <div className="text-[11px] text-slate-400">Arrastre los cuadrados blancos del trazado para moverlo, los azules para añadir puntos y clic derecho para borrar. Pulse «Recalcular plan» para actualizar señales y avisos.</div>}
  </div>
);

export const StaffPanel = ({ p, at, moved, canEdit, onReset, onOpenTask }) => (
  <div className="space-y-3" data-testid="staff-panel">
    <span className="font-mono text-[10px] tracking-widest text-sky-400">PERSONAL EN EL PUNTO</span>
    <Head title={p.tasks[0] || "Puesto"} sub={at != null ? `A las ${hm(at)}` : "Durante todo el evento"} />
    {Object.keys(STAFF_GROUPS).filter((g) => p.groups[g]).map((g) => (
      <div key={g} className="bg-[#0b1329] rounded-lg p-2.5" data-testid={`staff-panel-group-${g}`}>
        <div className="flex items-center gap-2 text-xs text-white font-semibold"><StaffMini group={g} size={16} />{STAFF_GROUPS[g].label} · {p.groups[g]}</div>
        <div className="mt-1.5 space-y-0.5">
          {!p.slots.length && (p.units?.[g] || []).map((u) => <div key={u} className="font-mono text-[11px] text-slate-300">{u}</div>)}
          {p.slots.filter((s) => s.g === g && (at == null || (s.start <= at && at < s.end))).map((s, i) => (
            <div key={i} className="flex justify-between font-mono text-[11px] text-slate-300"><span>{s.uid}</span><span>{hm(s.start)}–{hm(s.end)}</span></div>
          ))}
        </div>
      </div>
    ))}
    <div className="text-xs text-slate-400 space-y-1">
      {p.tasks.map((t, i) => <button key={i} onClick={() => onOpenTask(i)} className="block text-left hover:text-sky-300" data-testid={`staff-panel-task-${i}`}>· {t}</button>)}
    </div>
    {canEdit && (
      <div className="flex items-start gap-2 text-[11px] text-slate-400 bg-sky-500/5 border border-sky-500/20 rounded-lg p-2">
        <Move className="w-4 h-4 text-sky-400 shrink-0" />
        <span>Arrastre el icono en el mapa para mover el puesto. Las horas y las órdenes se editan en «Asignación de efectivos», bajo el mapa.</span>
      </div>
    )}
    {canEdit && moved && <Button size="sm" variant="outline" className="h-8" onClick={onReset} data-testid="staff-reset-button"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Volver a la posición original</Button>}
  </div>
);
